/**
 * dashboardStats.ts — Statistiques du Tableau de bord (parité legacy BB/FK), PURES.
 * Toutes les valeurs sont calculées des VRAIES commandes/produits — aucun chiffre figé.
 * Date de réf. injectable (`now`) → testable.
 */
import type { Order, OrderItem } from './database.types';
import { isAwaitingOnlinePayment } from './orders';

const PAID = new Set(['paid', 'onsite_collected']);
export function isPaidOrder(o: Pick<Order, 'payment_status'>): boolean {
  return PAID.has(o.payment_status ?? '');
}

/** Début de journée locale d'une date. */
function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/** Delta % arrondi, ou null si pas de base de comparaison (évite les faux %). */
export function pctDelta(cur: number, prev: number): number | null {
  if (!prev) return null;
  return Math.round(((cur - prev) / prev) * 100);
}

/** Texte d'un delta (« +12% » / « -5% » / "" si null). */
export function deltaText(p: number | null): string {
  if (p == null) return '';
  return (p >= 0 ? '+' : '') + p + '%';
}

export interface DaySlice {
  label: string;
  revenueCents: number;
  count: number;
}

export interface DashboardStats {
  todayCount: number;
  todayRevenueCents: number;
  inProgress: number;
  avgBasketCents: number;
  dOrders: number | null;
  dRevenue: number | null;
  dAvg: number | null;
  d7: number | null;
  revenue7dCents: number;
  last7: DaySlice[];
  topProducts: { name: string; qty: number }[];
  toAccept: number;
  outOfStock: string[];
}

function inRange(iso: string | null, a: Date, b: Date): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  return t >= a.getTime() && t < b.getTime();
}

/** Construit toutes les stats du dashboard à partir des vraies données. */
export function computeDashboardStats(
  orders: Order[],
  itemsByOrder: Record<string, OrderItem[]>,
  outOfStockNames: string[],
  now: Date = new Date(),
): DashboardStats {
  const today = startOfDay(now);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const yStart = new Date(today); yStart.setDate(yStart.getDate() - 1);

  const revOf = (list: Order[]) => list.filter(isPaidOrder).reduce((s, o) => s + (o.total_cents || 0), 0);

  const todayOrders = orders.filter((o) => inRange(o.created_at, today, tomorrow));
  const todayPaid = todayOrders.filter(isPaidOrder);
  const todayRevenueCents = revOf(todayOrders);
  const avgBasketCents = todayPaid.length > 0 ? Math.round(todayRevenueCents / todayPaid.length) : 0;
  const inProgress = orders.filter((o) => ['acceptee', 'en_cuisine', 'prete'].includes(o.status)).length;

  const yOrders = orders.filter((o) => inRange(o.created_at, yStart, today));
  const yPaid = yOrders.filter(isPaidOrder);
  const yRevenue = revOf(yOrders);
  const yAvg = yPaid.length > 0 ? Math.round(yRevenue / yPaid.length) : 0;

  const last7: DaySlice[] = [];
  for (let i = 6; i >= 0; i--) {
    const a = new Date(today); a.setDate(a.getDate() - i);
    const b = new Date(a); b.setDate(b.getDate() + 1);
    const dayOrders = orders.filter((o) => inRange(o.created_at, a, b));
    last7.push({
      label: a.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit' }),
      revenueCents: revOf(dayOrders),
      count: dayOrders.filter(isPaidOrder).length,
    });
  }
  const revenue7dCents = last7.reduce((s, d) => s + d.revenueCents, 0);
  const prevStart = new Date(today); prevStart.setDate(prevStart.getDate() - 13);
  const prevEnd = new Date(today); prevEnd.setDate(prevEnd.getDate() - 6);
  const prev7Rev = revOf(orders.filter((o) => inRange(o.created_at, prevStart, prevEnd)));

  const counts: Record<string, number> = {};
  Object.values(itemsByOrder).forEach((items) =>
    items.forEach((it) => { counts[it.name] = (counts[it.name] || 0) + (it.quantity || 0); }),
  );
  const topProducts = Object.entries(counts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([name, qty]) => ({ name, qty }));

  const toAccept = orders.filter((o) => o.status === 'recue' && !isAwaitingOnlinePayment(o)).length;

  return {
    todayCount: todayOrders.length,
    todayRevenueCents,
    inProgress,
    avgBasketCents,
    dOrders: pctDelta(todayOrders.length, yOrders.length),
    dRevenue: pctDelta(todayRevenueCents, yRevenue),
    dAvg: pctDelta(avgBasketCents, yAvg),
    d7: pctDelta(revenue7dCents, prev7Rev),
    revenue7dCents,
    last7,
    topProducts,
    toAccept,
    outOfStock: outOfStockNames,
  };
}
